/**
 * Oberflächentexte. Von Anfang an getrennt vom Code, auch wenn es zunächst nur Deutsch gibt:
 * Englisch kommt, sobald eine Organisation außerhalb des deutschen Sprachraums dazukommt,
 * und Nachrüsten kostet dann ein Vielfaches. Siehe design/06-designsystem.md 6.6.
 */
export const de = {
  app: {
    name: 'DoorAccessManager',
    loading: 'Lädt…',
    save: 'Speichern',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    close: 'Schließen',
    copy: 'Kopieren',
    copied: 'In die Zwischenablage kopiert.',
    none: '—',
    unlimited: 'unbefristet',
    noEntries: 'Keine Einträge.',
    loadingMore: 'weitere werden beim Scrollen geladen…',
    noMore: 'Keine weiteren Einträge.',
    retry: 'Erneut versuchen',
    all: 'Alle',
    active: 'aktiv',
    inactive: 'inaktiv',
  },

  nav: {
    dashboard: 'Dashboard',
    doorcodes: 'Türcodes',
    groups: 'Gruppen',
    doors: 'Türen',
    accounts: 'Benutzer',
    log: 'Protokoll',
    audit: 'Änderungen',
    organizations: 'Organisationen',
    organization: 'Organisation',
    profile: 'Profil',
    logout: 'Abmelden',
    leaveContext: 'Kontext verlassen',
    theme: 'Hell / Dunkel',
  },

  login: {
    title: 'Anmeldung',
    email: 'E-Mail',
    password: 'Passwort',
    rememberMe: 'Angemeldet bleiben',
    submit: 'Anmelden',
    // Immer dieselbe Meldung, auch bei unbekannter Adresse: sonst wird die Maske zum
    // Verzeichnis gültiger Adressen.
    failed: 'E-Mail oder Passwort falsch.',
    locked: 'Zu viele Fehlversuche. Bitte in {minutes} Minuten erneut versuchen.',
    disabled: 'Das Konto oder die Organisation ist deaktiviert.',
    forgotten: 'Passwort vergessen? Wenden Sie sich an Ihre Administration.',
    reveal: 'Aufdecken, solange gehalten',
  },

  passwordChange: {
    title: 'Passwort festlegen',
    forced: 'Sie haben ein Einmalpasswort erhalten und müssen jetzt ein eigenes festlegen.',
    current: 'Aktuelles Passwort',
    next: 'Neues Passwort',
    repeat: 'Wiederholen',
    minLength: 'Mindestens 12 Zeichen',
    lengthOk: 'Länge ausreichend',
    mismatch: 'Die Eingaben stimmen nicht überein.',
    saved: 'Passwort geändert. Alle anderen Sitzungen wurden beendet.',
  },

  dashboard: {
    title: 'Übersicht',
    doorCodes: 'Türcodes',
    doorsOnline: 'Türen aktuell',
    accessesToday: 'Zutritte heute',
    denied: 'Abgewiesen',
    expiringSoon: '{count} laufen in 14 Tagen ab',
    lastAccesses: 'Letzte Zutritte',
    showLog: 'Protokoll →',
    // Eine Tür ohne aktuellen Kontakt ist nicht ausgefallen: sie arbeitet mit ihrem letzten
    // Stand weiter und wird nur temporär nicht aktualisiert.
    staleWarning: '{name} hat seit {since} keine Codes abgerufen.',
    staleConsequence: 'Änderungen sind dort nicht wirksam — auch Sperren nicht.',
    firmwareWarning: '{name} meldet Firmware {version} und kann das aktuelle Codeformat nicht lesen.',
    firmwareConsequence:
      'Die Tür arbeitet mit ihrem letzten Stand weiter und übernimmt keine Änderungen. '
      + 'Die Firmware aktualisiert sich selbst zur eingestellten Zeit; manuell auslösen lässt sie sich nicht.',
    unknownCodes: '{count} unbekannte Codes an {door}',
  },

  doorcodes: {
    title: 'Türcodes',
    create: '+ Türcode anlegen',
    searchPlaceholder: 'Name oder Code…',
    filterGroup: 'Gruppe',
    filterStatus: 'Status',
    filterDoor: 'Tür',
    statusActive: 'Aktiv',
    statusInactive: 'Deaktiviert',
    hits: '{count} Treffer',
    columnName: 'Name',
    columnCode: 'Code',
    columnGroups: 'Gruppen',
    columnValidUntil: 'Gültig bis',
    columnKeyTag: 'Tag',
    noGroup: '— keine Gruppe',
    noGroupWarning: 'ohne Gruppe: dieser Code öffnet derzeit keine Tür',
    expiresInDays: 'läuft in {days} Tagen ab',
    hiddenGroups: '{count} weitere Gruppe(n), für Sie nicht sichtbar',
    firstName: 'Vorname',
    lastName: 'Nachname',
    label: 'Bezeichnung',
    labelHelp: 'Für unpersönliche Codes, zum Beispiel „Lieferdienst".',
    validFrom: 'Gültig von',
    validUntil: 'Gültig bis',
    unlimitedHint: 'Leer lassen für unbefristet.',
    accessCode: 'Zugangscode',
    regenerate: 'Code neu erzeugen',
    regenerateHint: 'Der alte Code wird ungültig, sobald die Türen den neuen abgerufen haben.',
    keyTag: 'Schlüsseltag',
    keyTagNone: 'kein Schlüsseltag zugewiesen',
    keyTagAppOnly: 'Schlüsseltags werden ausschließlich mit der DoorManager-App zugewiesen.',
    groupsAndTimes: 'Gruppen und Zeiten',
    addGroup: 'Gruppe hinzufügen',
    inheritSchedule: 'Gruppenplan übernehmen',
    narrowSchedule: 'weiter einschränken',
    effectiveAccess: 'Effektiver Zugriff',
    noAccessAtDoor: 'Kein Zugriff auf diese Tür.',
    probe: 'Punktprobe',
    probeRun: 'Prüfen',
    probeGranted: 'Zutritt',
    probeDenied: 'Kein Zutritt',
    history: 'Verlauf',
    showAll: 'Alle anzeigen →',
    createdBy: 'Angelegt von {name}',
    newCodeTitle: 'Türcode angelegt',
    newCodeHint: 'Der Code wird nur jetzt offen angezeigt. Bitte notieren oder weitergeben.',
    deactivate: 'Deaktivieren',
    activate: 'Aktivieren',
    confirmDelete: 'Türcode „{name}" löschen?',
    confirmDeleteDetail: 'Der Zugang wird an allen betroffenen Türen entzogen.',
    bulkTitle: '{count} ausgewählt',
    bulkAddGroup: 'Gruppe zuweisen',
    bulkRemoveGroup: 'Gruppe entfernen',
    bulkDeactivate: 'Deaktivieren',
    bulkActivate: 'Aktivieren',
    bulkDelete: 'Löschen',
    bulkResult: '{succeeded} geändert, {failed} abgelehnt.',
  },

  groups: {
    title: 'Gruppen',
    create: '+ Gruppe anlegen',
    intro: 'Eine Gruppe bündelt Türen und die Zeiten, zu denen sie geöffnet werden dürfen.',
    name: 'Name',
    description: 'Beschreibung',
    color: 'Farbe',
    doors: 'Türen',
    alsoIn: 'auch in: {groups}',
    schedule: 'Wochenplan',
    alwaysOpen: 'Durchgehend (24/7)',
    alwaysOpenHint: 'Diese Gruppe ist durchgehend offen. Haken entfernen, um Zeitfenster festzulegen.',
    templates: 'Vorlagen',
    templateOffice: 'Bürozeit',
    templateSchool: 'Schulzeit',
    templateEvening: 'Abend',
    gridHint: 'Ziehen zum Anlegen · Klick auf einen Block zum Löschen · Rastung 15 min',
    listHint: 'Die Liste bearbeitet denselben Zustand wie das Gitter und ist tastaturbedienbar.',
    addWindow: '+ Zeitfenster',
    counts: '{codes} Türcodes · {doors} Türen',
    noWindow: 'kein Zeitfenster',
    always: 'durchgehend (24/7)',
    impact: 'Auswirkung',
    impactUsers: '{codes} Türcodes nutzen diese Gruppe, davon {narrowed} mit eigener Einschränkung.',
    impactLosing: 'Durch die Änderung verlieren {count} Türcodes Zeitfenster:',
    impactGaining: '{count} Türcodes gewinnen Zeitfenster.',
    impactNone: 'Die Änderung nimmt keinem Türcode Zeitfenster.',
    confirmDelete: 'Gruppe „{name}" löschen?',
    confirmDeleteDetail:
      '{codes} Türcodes verlieren ihren Zugang zu {doors}. Die Türcodes selbst bleiben erhalten.',
  },

  doors: {
    title: 'Türen',
    create: '+ Tür hinzufügen',
    columnStatus: 'Status',
    columnName: 'Name',
    columnLocation: 'Standort',
    columnGroups: 'Gruppen',
    columnCodes: 'Codes',
    columnFirmware: 'Firmware',
    statusLive: 'aktuell',
    statusStale: 'nicht aktuell',
    statusWaiting: 'wartet auf ersten Abruf',
    statusInactive: 'deaktiviert',
    lastFetch: 'letzter Abruf {when}',
    // Für die Erwartungshaltung wichtig: eine offline Tür ist kein Ausfall.
    staleExplain: 'Die Tür öffnet weiter, wird aber temporär nicht aktualisiert.',
    waitingExplain: 'Noch kein Abruf. Bitte Tür-ID und Schlüssel prüfen.',
    name: 'Bezeichnung',
    location: 'Standort',
    doorID: 'Tür-ID',
    codeKey: 'Schlüssel',
    codeKeyProgress: '{entered} von 64 Zeichen',
    codeKeySet: 'Schlüssel hinterlegt',
    replaceCodeKey: 'Schlüssel ersetzen',
    serialHint:
      'Beide Werte gibt der Controller beim ersten Start auf der seriellen Konsole aus: '
      + '„ID ok: …" und „Door code key: …".',
    updateTime: 'Update-Zeit',
    updateTimeHint: 'Uhrzeit für das tägliche Firmware-Update, Ortszeit.',
    firmwareReadOnly: 'Die Firmware aktualisiert sich selbst; manuell auslösen lässt sie sich nicht.',
    status: 'Status',
    payloadVersion: 'Payload-Version',
    deliveredCodes: 'Ausgelieferte Codes',
    codeLimitWarning:
      'An dieser Tür werden {count} von {max} Codes ausgeliefert. Nicht mehr benötigte Codes '
      + 'sollten deaktiviert werden.',
    droppedCodes: '{count} Codes werden wegen einer Grenze nicht ausgeliefert.',
    whoWhen: 'Wer kommt wann',
    whoWhenHint: 'Überlagerte Gruppenbänder. Macht Auslegungsfehler sichtbar.',
    confirmDelete: 'Tür „{name}" löschen?',
    confirmDeleteDetail:
      'Die Tür wird aus allen Gruppen entfernt. Zutrittsereignisse bleiben im Protokoll erhalten.',
  },

  accounts: {
    title: 'Benutzer',
    create: '+ Benutzer anlegen',
    intro: 'Konten für die Anmeldung. Nicht zu verwechseln mit Türcodes.',
    columnName: 'Name',
    columnEmail: 'E-Mail',
    columnRole: 'Rolle',
    columnVisibility: 'Sichtbarkeit',
    columnLastLogin: 'Letzte Anmeldung',
    never: 'nie',
    neverHint: 'Das Einmalpasswort wurde nie eingelöst.',
    locked: 'gesperrt',
    roleAdmin: 'Admin',
    roleUser: 'User',
    roleAdminHelp: 'steuert die gesamte Organisation: Türen, Gruppen, Zeitpläne, Benutzer',
    roleUserHelp: 'pflegt Türcodes',
    visibility: 'Sichtbarkeit',
    visibilityOpen: 'Alle Türen der Organisation',
    visibilityRestricted: 'Nur folgende Gruppen',
    resolvedDoors: 'Sieht damit: {doors}',
    emailHelp: 'Dient als Anmeldename.',
    oneTimePassword: 'Einmalpasswort',
    oneTimePasswordHint: 'Wird einmal angezeigt und muss bei der ersten Anmeldung geändert werden.',
    resetPassword: 'Passwort zurücksetzen',
    revokeSessions: 'Alle Sitzungen beenden',
    confirmDelete: 'Benutzer „{name}" löschen?',
    confirmDeleteDetail: 'Das Konto und alle seine Sitzungen werden entfernt.',
  },

  log: {
    title: 'Zutrittsprotokoll',
    timesIn: 'Alle Zeiten in {timezone}',
    columnTime: 'Zeit',
    columnDoor: 'Tür',
    columnDoorCode: 'Türcode',
    columnMethod: 'Art',
    columnResult: 'Ergebnis',
    methodCode: 'Code',
    methodKeyTag: 'Tag',
    granted: 'Zutritt',
    denied: 'abgewiesen',
    unknownCode: 'unbekannt',
    export: 'CSV',
    filterResult: 'Ergebnis',
    resultGranted: 'Nur Zutritte',
    resultDenied: 'Nur Abweisungen',
    reportedLate: 'nachgeliefert',
    reportedLateHint: 'Die Tür hat dieses Ereignis offline gepuffert und später gemeldet.',
    retention: 'Ereignisse werden nicht automatisch gelöscht.',
    restrictedHint: 'Gefiltert auf Ihre Gruppen.',
    restrictedUnknownHint:
      'Abgewiesene Versuche mit unbekanntem Code haben keinen Türcode und sind daher hier nicht '
      + 'sichtbar. Die Anzahl steht auf dem Dashboard.',
  },

  audit: {
    title: 'Änderungsprotokoll',
    intro: 'Wer hat was erlaubt — getrennt vom Zutrittsprotokoll, das zeigt, wer hineinging.',
    columnTime: 'Zeit',
    columnActor: 'Wer',
    columnObject: 'Objekt',
    columnAction: 'Änderung',
  },

  organizations: {
    title: 'Organisationen',
    create: '+ Organisation anlegen',
    columnName: 'Name',
    columnDoors: 'Türen',
    columnDoorCodes: 'Türcodes',
    columnAccounts: 'Benutzer',
    columnActivity: 'Letzte Aktivität',
    enter: 'In Organisation wechseln',
    name: 'Name',
    timezone: 'Zeitzone',
    timezoneHint:
      'Gilt für alle Wochenpläne und Zeitanzeigen — unabhängig von der Zeitzone des Browsers.',
    initialAdmin: 'Erster Admin',
    initialAdminHint: 'Pflicht: eine Organisation ohne Admin ist nicht benutzbar.',
    noSettingsHint:
      'Es gibt bewusst keine Einstellung für die Aufbewahrung von Zutrittsereignissen und keine '
      + 'für das Verhalten offline Türen. Ereignisse werden nicht automatisch gelöscht, und Türen '
      + 'arbeiten offline immer weiter.',
    confirmDelete: 'Organisation „{name}" löschen?',
    confirmDeleteDetail:
      'Türen, Gruppen, Türcodes, Benutzer und Protokolle dieser Organisation werden entfernt. '
      + 'Zur Bestätigung den Namen eingeben.',
    contextActive: 'Sie arbeiten in: {name}',
  },

  profile: {
    title: 'Profil',
    changePassword: 'Passwort ändern',
    sessions: 'Aktive Sitzungen',
    sessionsHint: 'Hier lässt sich ein verlorenes Gerät abmelden.',
    currentSession: 'diese Sitzung',
    endSession: 'Beenden',
    endOthers: 'Alle anderen beenden',
  },

  schedule: {
    days: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
    groupPlan: 'Gruppenplan',
    effective: 'wirksam',
    clipped: 'durch Gruppe begrenzt',
    midnightSplit: 'Über Mitternacht gezogene Fenster werden in zwei Regeln zerlegt.',
  },

  errors: {
    generic: 'Es ist ein Fehler aufgetreten.',
    forbidden: 'Dafür fehlt die Berechtigung.',
    notFound: 'Nicht gefunden.',
    keytagAppOnly: 'Schlüsseltags werden ausschließlich mit der App zugewiesen.',
    lastAdmin: 'Das ist der letzte aktive Admin dieser Organisation.',
    selfDelete: 'Das eigene Konto kann nicht gelöscht oder deaktiviert werden.',
    duplicateName: 'Der Name ist bereits vergeben.',
    invalidCodeKey: 'Der Schlüssel ist nicht gültig.',
  },
}

/** Ersetzt {platzhalter} im Text. */
export function fmt(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match)
}
